import * as React from 'react'

import { PdfLinkOverlay } from './pdf-viewer.types'
import { usePdfViewerContext } from './pdf-viewer-provider'

export function PdfViewerCanvas() {
  const { alignment, currentPage, navigateToDestination, renderCurrentPage, renderKey, theme } = usePdfViewerContext()
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const [linkOverlays, setLinkOverlays] = React.useState<PdfLinkOverlay[]>([])

  React.useEffect(() => {
    let cancelled = false

    const render = async () => {
      if (!canvasRef.current) {
        return
      }

      try {
        const overlays = await renderCurrentPage(canvasRef.current)
        if (!cancelled) {
          setLinkOverlays(overlays)
        }
      } catch {
        if (!cancelled) {
          setLinkOverlays([])
        }
      }
    }

    render()

    return () => {
      cancelled = true
    }
  }, [currentPage, renderCurrentPage, renderKey])

  const isSystemDark =
    theme === 'system' && typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false
  const backgroundClass =
    theme === 'dark' || isSystemDark ? 'bg-zinc-900' : theme === 'light' ? 'bg-zinc-200' : 'bg-zinc-200'
  const alignmentClass =
    alignment === 'left' ? 'justify-start' : alignment === 'right' ? 'justify-end' : 'justify-center'

  return (
    <div className={`h-full overflow-y-auto px-3 pb-16 pt-10 md:px-8 ${backgroundClass}`}>
      <div className={`flex min-h-full items-start ${alignmentClass}`}>
        <div className="relative inline-block border border-black/10 bg-white shadow-lg">
          <canvas ref={canvasRef} className="block" />
          <div className="absolute inset-0 z-10">
            {linkOverlays.map((overlay, index) => (
              <button
                key={`${overlay.left}-${overlay.top}-${index}`}
                aria-label="PDF link"
                className="absolute cursor-pointer bg-transparent hover:bg-yellow-200/20"
                style={{
                  left: `${overlay.left}px`,
                  top: `${overlay.top}px`,
                  width: `${overlay.width}px`,
                  height: `${overlay.height}px`,
                }}
                onClick={async event => {
                  event.preventDefault()
                  event.stopPropagation()

                  if (overlay.url) {
                    window.open(overlay.url, '_blank', 'noopener,noreferrer')
                    return
                  }

                  await navigateToDestination(overlay.dest ?? null)
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
